import { computed, inject, Injectable, PLATFORM_ID, signal } from "@angular/core";
import { Articles, IResumeTips, ISidebarData } from "../interfaces/resume-tips";
import { isPlatformBrowser } from "@angular/common";
import { TranslocoService } from "@jsverse/transloco";
import { toSignal } from "@angular/core/rxjs-interop";

export interface ICategoryNavItem {
    label: string;
    link?: string;
}

@Injectable({
    providedIn: 'root'
})
export class StructureSidebarService {
    private readonly platformId = inject(PLATFORM_ID);
    private readonly STORAGE_KEY = 'structure_data';
    sidebarData = signal<ISidebarData | null>(this.getSidebarData());
    private readonly CATEGORY_NAV_KEY = 'category_nav_data';
    private readonly transloco = inject(TranslocoService);
    private readonly STORAGE2nd_KEYs = 'sidebar_persistent_data';


    categoryHeader = signal<string>('');
    categoryNavItems = signal<ICategoryNavItem[]>([]);

    showGoogleAd1 = signal<boolean>(false);
    showWizardsAd = signal<boolean>(false);
    showGoogleAd2 = signal<boolean>(false);

    setCategoryData(header: string, items: ICategoryNavItem[]): void {
        this.categoryHeader.set(header);
        this.categoryNavItems.set(items);
        if (isPlatformBrowser(this.platformId)) {
            localStorage.setItem(this.CATEGORY_NAV_KEY, JSON.stringify({ header, items }));
        }
    }

    recoverCategoryData(): void {
        if (isPlatformBrowser(this.platformId)) {
            const data: string | null = localStorage.getItem(this.CATEGORY_NAV_KEY);
            if (data !== null && data.length > 0) {
                const parsed = JSON.parse(data);
                this.categoryHeader.set(parsed.header);
                this.categoryNavItems.set(parsed.items);
            }
        }
    }

    clearCategoryData(): void {
        this.categoryHeader.set('');
        this.categoryNavItems.set([]);
        if (isPlatformBrowser(this.platformId)) {
            localStorage.removeItem(this.CATEGORY_NAV_KEY);
        }
    }

    updateSidebar(data: ISidebarData): void {
        if (isPlatformBrowser(this.platformId)) {
            localStorage.setItem(this.STORAGE_KEY, JSON.stringify(data));
        }
        this.sidebarData.set(data);
    }

    private getSidebarData(): ISidebarData | null {
        if (isPlatformBrowser(this.platformId)) {
            const data = localStorage.getItem(this.STORAGE_KEY);
            return data !== null ? JSON.parse(data) as ISidebarData : null;
        }
        return null;
    }

    private readonly lang = toSignal(this.transloco.langChanges$, {
        initialValue: this.transloco.getActiveLang()
    });

    private readonly enData = signal<IResumeTips[] | Articles[] | null>(this.getStoredData('en'));
    private readonly bnData = signal<IResumeTips[] | Articles[] | null>(this.getStoredData('bn'));
    private readonly enHeader = signal<string>(this.getStoredHeader('en') ?? 'Cover Letter');
    private readonly bnHeader = signal<string>(this.getStoredHeader('bn') ?? 'কভার লেটার');

    readonly SeconsidebarData = computed(() => {
        const currentLang = this.lang();
        const data = currentLang === 'en' ? this.enData() : this.bnData();
        const header = currentLang === 'en' ? this.enHeader() : this.bnHeader();

        if (!data) return null;

        return { header, data };
    });

    setSidebarConfig(en: IResumeTips[] | Articles[], bn: IResumeTips[] | Articles[], enHeader: string, bnHeader: string): void {

        this.enData.set(en);
        this.bnData.set(bn);
        this.enHeader.set(enHeader);
        this.bnHeader.set(bnHeader);


        if (isPlatformBrowser(this.platformId)) {
            const storageObj = { en, bn, enHeader, bnHeader };
            localStorage.setItem(this.STORAGE2nd_KEYs, JSON.stringify(storageObj));
        }
    }


    private getStoredData(lang: 'en' | 'bn'): IResumeTips[] | Articles[] | null {
        if (isPlatformBrowser(this.platformId)) {
            const saved = localStorage.getItem(this.STORAGE2nd_KEYs);
            if (saved !== null && saved.length > 0) {
                const parsed = JSON.parse(saved);
                return lang === 'en' ? parsed.en : parsed.bn;
            }
        }
        return null;
    }


    private getStoredHeader(lang: 'en' | 'bn'): string | null {
        if (isPlatformBrowser(this.platformId)) {
            const saved = localStorage.getItem(this.STORAGE2nd_KEYs);
            if (saved !== null && saved.length > 0) {
                const parsed = JSON.parse(saved);
                return lang === 'en' ? parsed.enHeader : parsed.bnHeader;
            }
        }
        return null;
    }


    setCollapsed(title: string, state: boolean): void {

        this.enData.update(data =>
            (data?.map(item => {
                const article = item as Articles;
                return article.articleHeader === title
                    ? { ...article, collapsed: state }
                    : item;
            }) ?? null) as Articles[] | null
        );


        this.bnData.update(data =>
            (data?.map(item => {
                const article = item as Articles;
                return article.articleHeader === title
                    ? { ...article, collapsed: state }
                    : item;
            }) ?? null) as Articles[] | null
        );


        if (isPlatformBrowser(this.platformId)) {
            const saved = localStorage.getItem(this.STORAGE2nd_KEYs);
            if (saved !== null && saved.length > 0) {
                const parsed = JSON.parse(saved);
                parsed.en = this.enData();
                parsed.bn = this.bnData();
                localStorage.setItem(this.STORAGE2nd_KEYs, JSON.stringify(parsed));
            }
        }


    }

}